import { c as _c } from "react/compiler-runtime";
import { SelectProjectRole } from "@components/role/select-project-role";
import { Box } from "@mui-wrapper";
import { Text, Tooltip } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useProjectRoleXgSeat } from "@hooks/projects/use-project-role-xg-seat";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { useCompanyId } from "@context-providers/company-id-provider";
import { CHANGE_ROLE_THROUGH_TEAM_MESSAGE } from "@src/constants/team-constants";
import { updateProjectRoleInMap } from "@store/company-member-groups/company-member-groups-slice";
import { useAppDispatch } from "@store/store-helper";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { formatUserRoleType, isProjectAssignedThroughTeam } from "@utils/data-display";
/**
 * Role cell for the member overview projects table. Renders an editable role
 * dropdown for direct assignments, and a read-only label (with an explanatory
 * tooltip) when the member's access is inherited through a team. Where the role
 * comes from is shown by the separate access column.
 */
export function MemberGroupProjectRoleCell(t0) {
  const $ = _c(26);
  const {
    project,
    member,
    onRoleChanged
  } = t0;
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    canChangeMemberProjectRole
  } = useHasUserValidRoleCompanyLevel();
  const {
    canAssignProjectRoles
  } = useHasUserValidFeatureCompanyLevel();
  const t1 = project.role ?? "";
  let t2;
  if ($[0] !== project.id) {
    t2 = {
      id: project.id
    };
    $[0] = project.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = t2;
  let t4;
  if ($[2] !== companyId || $[3] !== member.identity || $[4] !== t1 || $[5] !== t3) {
    t4 = {
      companyId,
      location: "member-group-project-role-column",
      subjectType: "project",
      memberIdentity: member.identity,
      currentRole: t1,
      selectedProject: t3
    };
    $[2] = companyId;
    $[3] = member.identity;
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    changeMemberRole
  } = useChangeMemberRole(t4);
  const {
    requestRoleChange,
    xgSeatDialog,
    disabledRoles
  } = useProjectRoleXgSeat({
    member,
    currentRole: project.role,
    applyRoleChange
  });
  if (!project.role) {
    return null;
  }
  let t5;
  if ($[7] !== project.role) {
    t5 = formatUserRoleType(project.role);
    $[7] = project.role;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const roleLabel = t5;
  const isAssignedThroughTeam = isProjectAssignedThroughTeam(project);
  const isResourceOwner = project.role === "RESOURCE_OWNER";
  const canEditRole = canChangeMemberProjectRole && canAssignProjectRoles && !isAssignedThroughTeam && !isResourceOwner;
  if (!canEditRole) {
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        fontSize: DEFAULT_TEXT_FONT_SIZE
      };
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== roleLabel) {
      t7 = <Text variant="bodyS" sx={t6}>{roleLabel}</Text>;
      $[10] = roleLabel;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    const label = t7;
    if (isAssignedThroughTeam) {
      let t8;
      if ($[12] !== label) {
        t8 = <Tooltip component="div" title={CHANGE_ROLE_THROUGH_TEAM_MESSAGE}>{label}</Tooltip>;
        $[12] = label;
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      return t8;
    }
    if (isResourceOwner) {
      let t8;
      if ($[14] !== label) {
        t8 = <Tooltip component="div" title="To change the project manager, navigate to the Management section from the three-dot menu.">{label}</Tooltip>;
        $[14] = label;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      return t8;
    }
    return label;
  }
  async function applyRoleChange(role) {
    try {
      await changeMemberRole(role);
      dispatch(updateProjectRoleInMap({
        projectId: project.id,
        newRole: role
      }));
      await onRoleChanged();
    } catch {}
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      display: "flex",
      alignItems: "center",
      height: "100%"
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = project.role;
  let t8;
  if ($[17] !== requestRoleChange) {
    t8 = role_0 => void requestRoleChange(role_0);
    $[17] = requestRoleChange;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== disabledRoles || $[20] !== t7 || $[21] !== t8) {
    t9 = <SelectProjectRole selectedRole={t7} onChange={t8} variant="borderless" disabledRoles={disabledRoles} />;
    $[19] = disabledRoles;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t9 || $[24] !== xgSeatDialog) {
    t10 = <Box component="div" onClick={_temp} sx={t6}>{t9}{xgSeatDialog}</Box>;
    $[23] = t9;
    $[24] = xgSeatDialog;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
function _temp(event) {
  return event.stopPropagation();
}